import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { Card } from "../components/ui/Card.js";
import { Badge } from "../components/ui/Badge.js";
import { Textarea } from "../components/ui/Textarea.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.js";
import { useToast } from "../hooks/use-toast.js";
import { useMutation } from "@apollo/client";
import { ADD_WAITLIST_ENTRY } from "../lib/graphqlOperations.js";
import { Navbar } from "../components/layout/Navbar.js";
import Footer from "../components/layout/Footer.js";
import logo from "../assets/stock_images/logo.png";
import indianVendor from "../assets/stock_images/indian_local_vendor__c8992913.jpg";
import {
  Store,
  ArrowRight,
  Layers,
  Globe,
  TrendingUp,
  Heart,
  Target,
  Rocket,
  Building2,
  Clock,
  Check,
  Truck,
  Code,
  Headphones,
  MapPin,
  Package,
} from "lucide-react";

const platformLayers = [
  {
    icon: Code,
    title: "Tech Layer",
    description: "Modern infrastructure enabling any local vendor to go online instantly with zero technical knowledge required.",
  },
  {
    icon: Headphones,
    title: "Service Layer",
    description: "End-to-end support for vendors - from onboarding to order management to customer service.",
  },
  {
    icon: Truck,
    title: "Logistics Layer",
    description: "Hyperlocal delivery network optimized for under 10-minute delivery from any neighborhood shop.",
  },
];

const whyInvest = [
  {
    icon: Store,
    title: "Local-First Network",
    description: "Unlike dark stores, we empower existing local vendors. More sustainable, more community-focused, more scalable.",
  },
  {
    icon: Globe,
    title: "Global Reach",
    description: "Optimized for local commerce but delivering goods globally. The only platform with this unique positioning.",
  },
  {
    icon: Rocket,
    title: "Rapid Scaling Model",
    description: "No inventory costs, no dark store capex. We scale with vendors, not against them.",
  },
  {
    icon: Heart,
    title: "Community Impact",
    description: "Every order supports a local family business. Social impact built into the business model.",
  },
];

const investmentRanges = [
  { value: "under_10L", label: "Under 10 Lakhs" },
  { value: "10L_50L", label: "10 - 50 Lakhs" },
  { value: "50L_1Cr", label: "50 Lakhs - 1 Crore" },
  { value: "above_1Cr", label: "Above 1 Crore" },
];

export default function Investors() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [investmentRange, setInvestmentRange] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const [addWaitlistEntry, { loading: submitPending }] = useMutation(ADD_WAITLIST_ENTRY, {
    onCompleted: (data) => {
      if (data?.addWaitlistEntry?.success) {
        toast({
          title: "Thank you for your interest!",
          description: "We'll reach out with exclusive updates and details.",
        });
        setIsSubmitted(true);
        setName("");
        setPhone("");
        setInvestmentRange("");
        setNotes("");
      } else {
        const message = data?.addWaitlistEntry?.message || "Something went wrong. Please try again.";
        toast({
          title: "Something went wrong",
          description: message,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Something went wrong",
        description: error.message || "Please enter a valid name and phone number.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name && phone) {
      const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
      if (cleanPhone.length !== 10) {
        toast({
          title: "Invalid Phone",
          description: "Please enter a valid 10-digit phone number.",
          variant: "destructive",
        });
        return;
      }
      addWaitlistEntry({
        variables: {
          name,
          phone: cleanPhone,
          entryType: "investor",
          location: investmentRange || undefined,
          message: notes || undefined,
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />

      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[5%] -right-[5%] w-[45%] h-[45%] bg-primary/5 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-accent/10 rounded-full blur-[120px]"
        />
      </div>

      <div className="relative z-10">
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <Badge variant="secondary" className="px-4 py-2">
                <TrendingUp className="w-4 h-4 mr-2" />
                Investor Relations
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight">
                Building the Future of{" "}
                <span className="text-primary">Local Commerce in India</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                We're building the tech layer for quick commerce that empowers millions of local vendors.
                Dark stores aren't the answer for India. Nurturing independent vendors is how India will flourish.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" onClick={() => document.getElementById('invest-form')?.scrollIntoView({ behavior: 'smooth' })}>
                  Join as Investor
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" size="lg" onClick={() => document.getElementById('vision')?.scrollIntoView({ behavior: 'smooth' })}>
                  Learn More
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-primary" />
                  <span>Empowering local vendors</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>Quick delivery promise</span>
                </div>
              </div>
            </div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 group">
                <img
                  src={indianVendor}
                  alt="Indian local vendor serving customers"
                  className="w-full h-auto object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-10 left-10 right-10">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Badge variant="secondary" className="bg-white/20 hover:bg-white/30 text-white backdrop-blur-md border border-white/10 px-4 py-2 mb-4">
                       Empowering Millions
                    </Badge>
                    <p className="text-white text-2xl font-display font-bold leading-tight">
                      Reimagining the $800B+ retail landscape of digital India.
                    </p>
                  </motion.div>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/20 rounded-full blur-[60px]" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-accent/20 rounded-full blur-[60px]" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop"
                  alt="Indian kirana store with fresh produce"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <Badge variant="secondary" className="px-4 py-2">
                <Target className="w-4 h-4 mr-2" />
                Our Vision
              </Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold">
                Dark Stores Are Not the Answer for India
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  India has millions of local vendors - kirana stores, restaurants, hardware shops, dairy farms.
                  They're the backbone of our communities. Quick commerce dark stores threaten their existence.
                </p>
                <p>
                  <strong className="text-foreground">We believe in a different future.</strong> One where technology
                  empowers these vendors instead of replacing them. Where quick commerce and local commerce coexist.
                </p>
                <p>
                  Quro is building the infrastructure that turns every local shop into a quick commerce hub.
                  Same speed, same convenience - but supporting real families and real communities.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-primary" />
                  <span>No inventory costs</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-primary" />
                  <span>No dark store capex</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-primary" />
                  <span>Community impact built-in</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Layers Section */}
      <section id="platform" className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <Badge variant="secondary" className="px-4 py-2 mb-4">
              <Layers className="w-4 h-4 mr-2" />
              Platform Architecture
            </Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              The Complete Stack for Local Commerce
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We're building every layer needed to transform local vendors into quick commerce powerhouses.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {platformLayers.map((layer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-10 rounded-3xl bg-card border border-border/50 shadow-soft hover:-translate-y-2 transition-transform duration-500 group"
              >
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 mx-auto group-hover:bg-primary/20 transition-colors">
                  <layer.icon className="w-10 h-10 text-primary" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-4">{layer.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{layer.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Invest Section */}
      <section id="why-invest" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Investment Thesis
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Why Quro is positioned to become the defining platform for local commerce in India.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 text-foreground">
            {whyInvest.map((item, index) => (
              <Card key={index} className="p-6 flex gap-5 border-border/50 shadow-md">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold">
              The Market Opportunity
            </h2>
            <div className="grid md:grid-cols-3 gap-8 py-8">
              <div className="space-y-2">
                <div className="text-5xl md:text-6xl font-bold">12M+</div>
                <p className="text-primary-foreground/80">Kirana stores in India</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl md:text-6xl font-bold">$800B</div>
                <p className="text-primary-foreground/80">Retail market size</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl md:text-6xl font-bold">2%</div>
                <p className="text-primary-foreground/80">Currently digitized</p>
              </div>
            </div>
            <p className="text-xl text-primary-foreground/80 max-w-3xl mx-auto">
              The quick commerce market is growing 50%+ YoY. But dark stores can only scale so far.
              The real opportunity lies in enabling the millions of existing local vendors.
            </p>
          </div>
        </div>
      </section>

      {/* Investor Form Section */}
      <section id="invest-form" className="py-16 md:py-24 text-foreground">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8 md:p-12 border-border/50 shadow-md">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="px-4 py-2 mb-4">
                <Building2 className="w-4 h-4 mr-2" />
                Investor Access
              </Badge>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                Join as an Investor
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Get exclusive access to our growth updates, operational metrics, and early investment opportunities.
                Plus, enjoy free delivery as a thank you for your interest.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4 text-left">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Your Name</label>
                    <Input
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Phone Number</label>
                    <Input
                      type="tel"
                      placeholder="Enter your phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2 text-left">
                  <label className="text-sm font-medium">Investment Range (Optional)</label>
                  <Select value={investmentRange} onValueChange={setInvestmentRange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select investment range" />
                    </SelectTrigger>
                    <SelectContent>
                      {investmentRanges.map((range) => (
                        <SelectItem key={range.value} value={range.value}>
                          {range.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 text-left">
                  <label className="text-sm font-medium">Additional Notes (Optional)</label>
                  <Textarea
                    placeholder="Tell us about your investment interests or any questions"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={submitPending || !name || !phone}
                >
                  {submitPending ? "Submitting..." : "Get Investor Access"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  By submitting, you'll receive exclusive updates as we scale operations.
                  Your information is kept confidential.
                </p>
              </form>
            ) : (
              <div className="text-center py-8 space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">Thank You for Your Interest!</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  We've received your information and will reach out with exclusive updates,
                  operational metrics, and investment details. You're now eligible for free delivery!
                </p>
                <Button variant="outline" onClick={() => setIsSubmitted(false)}>
                  Submit Another Interest
                </Button>
              </div>
            )}
          </Card>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
